import { useEffect, useMemo, useState } from "react";
import { computeCityPrices, formatMoney } from "@/lib/gold-data";
import { useGoldRates } from "@/lib/nonblocking-rates";
import { CalculatorActions, readCalculatorQuery } from "@/components/calculator/CalculatorActions";

type Country = "india" | "pakistan";
const DEFAULTS = { country: "india", monthly: "5000", years: "10", rate: "10" };

/** Gold SIP: fixed monthly purchase, compounded monthly at an assumed annual gold return. */
export function GoldSipCalculator() {
  const { data: rates } = useGoldRates();
  const [v, setV] = useState<Record<string, string>>(DEFAULTS);
  useEffect(() => {
    const q = readCalculatorQuery("sip", Object.keys(DEFAULTS));
    if (Object.keys(q).length) setV((p) => ({ ...p, ...q }));
  }, []);
  const set = (k: string, val: string) => setV((p) => ({ ...p, [k]: val }));

  const country: Country = v.country === "pakistan" ? "pakistan" : "india";
  const currency = country === "pakistan" ? "PKR" : "INR";
  const perGram = computeCityPrices({
    spotUsdPerOz: rates.spotUsdPerOz,
    fxRate: country === "pakistan" ? rates.pkrPerUsd : rates.inrPerUsd,
    country,
    cityPremium: 1,
  }).perGram24;

  const r = useMemo(() => {
    const P = Math.min(1e8, Math.max(0, Number(v.monthly) || 0));
    const years = Math.min(40, Math.max(1, Math.round(Number(v.years) || 1)));
    const annual = Math.min(30, Math.max(-10, Number(v.rate) || 0));
    const i = annual / 100 / 12;
    const n = years * 12;
    const fv = i === 0 ? P * n : P * ((Math.pow(1 + i, n) - 1) / i) * (1 + i);
    const invested = P * n;
    const yearly = Array.from({ length: years }, (_, y) => {
      const m = (y + 1) * 12;
      const val = i === 0 ? P * m : P * ((Math.pow(1 + i, m) - 1) / i) * (1 + i);
      return { year: y + 1, invested: P * m, value: val };
    });
    return { P, years, annual, i, n, fv, invested, gain: fv - invested, yearly };
  }, [v]);

  const gramsToday = perGram > 0 ? r.fv / perGram : 0;
  const max = Math.max(1, ...r.yearly.map((y) => y.value));
  const resultText = `Gold SIP (${currency}) — ${formatMoney(r.P, currency)}/month for ${r.years} years at ${r.annual}%: invested ${formatMoney(r.invested, currency)}, estimated value ${formatMoney(r.fv, currency)}, gain ${formatMoney(r.gain, currency)}.`;
  const field = "mt-1.5 w-full h-11 rounded-md border border-input bg-background px-3 text-base";

  return (
    <div className="rounded-3xl border border-border bg-card p-5 md:p-7">
      <h2 className="text-2xl font-bold">Gold SIP Calculator</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Estimate what a fixed monthly gold purchase (digital gold, gold ETF or gold savings scheme) could grow to.
      </p>
      <div className="mt-5 grid gap-6 md:grid-cols-[1fr_1.3fr]">
        <div className="space-y-4">
          <label className="block text-sm font-medium">Currency
            <select value={country} onChange={(e) => set("country", e.target.value)} className={field}>
              <option value="india">India (₹ INR)</option>
              <option value="pakistan">Pakistan (Rs PKR)</option>
            </select>
          </label>
          <label className="block text-sm font-medium">Monthly investment ({currency})
            <input type="number" min={0} inputMode="decimal" value={v.monthly} onChange={(e) => set("monthly", e.target.value)} className={field} />
          </label>
          <label className="block text-sm font-medium">Period (years)
            <input type="number" min={1} max={40} value={v.years} onChange={(e) => set("years", e.target.value)} className={field} />
          </label>
          <label className="block text-sm font-medium">Expected annual gold return (%)
            <input type="number" min={-10} max={30} step="0.1" value={v.rate} onChange={(e) => set("rate", e.target.value)} className={field} />
            <span className="mt-1 block text-[11px] font-normal text-muted-foreground">
              Gold's long-run average in rupee terms has been roughly 9–11% a year, but single years swing from −10% to +30%.
            </span>
          </label>
          <CalculatorActions values={v} resultText={resultText} onReset={() => setV(DEFAULTS)} queryPrefix="sip" />
        </div>
        <div className="min-h-[360px]">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl bg-muted/40 p-3"><div className="text-[11px] uppercase text-muted-foreground">Invested</div><div className="font-bold tabular-nums">{formatMoney(r.invested, currency)}</div></div>
            <div className="rounded-xl bg-muted/40 p-3"><div className="text-[11px] uppercase text-muted-foreground">Est. gain</div><div className="font-bold tabular-nums">{formatMoney(r.gain, currency)}</div></div>
            <div className="rounded-xl bg-primary p-3 text-primary-foreground"><div className="text-[11px] uppercase opacity-80">Est. value</div><div className="font-black tabular-nums">{formatMoney(r.fv, currency)}</div></div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            ≈ {gramsToday.toFixed(2)} g of 24K gold at today's rate ({formatMoney(perGram, currency)}/g).
          </p>
          <div className="mt-4 flex h-48 items-end gap-1" role="img" aria-label="Year-by-year SIP value">
            {r.yearly.map((y) => (
              <div key={y.year} className="relative flex-1" style={{ height: "100%" }} title={`Year ${y.year}: ${formatMoney(y.value, currency)}`}>
                <div className="absolute bottom-0 w-full rounded-t bg-primary/30" style={{ height: `${(y.value / max) * 100}%` }} />
                <div className="absolute bottom-0 w-full rounded-t bg-primary" style={{ height: `${(y.invested / max) * 100}%` }} />
              </div>
            ))}
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">Dark = money invested · light = estimated value, per year.</p>
          <details className="mt-3 rounded-xl border border-border p-3 text-sm">
            <summary className="cursor-pointer font-semibold">Step-by-step calculation</summary>
            <ol className="mt-2 list-decimal space-y-1 pl-5 font-mono text-xs">
              <li>Monthly rate i = {r.annual}% ÷ 12 = {(r.i * 100).toFixed(4)}%</li>
              <li>Instalments n = {r.years} × 12 = {r.n}</li>
              <li>FV = P × [((1+i)^n − 1) ÷ i] × (1+i)</li>
              <li>FV = {formatMoney(r.P, currency)} × … = {formatMoney(r.fv, currency)}</li>
            </ol>
          </details>
        </div>
      </div>
    </div>
  );
}
